// Company details card — the company's identity across the console, reports and
// the mobile app. Saves through POST companies/company-details, which takes the
// FULL object rather than a diff, so every writable field rides along on save.
//
// Code is the only locked row: it is server-issued and absent from the payload.
//
// Industry and Country are selects over fixed lists even though the contract
// types both as free strings. Industry stores the mocks/onboarding id
// ('retail'), matching what CompanyCreation already POSTs as businessIndustry;
// Country stores the English name, since there is no id vocabulary for it.
// Labels are translated in both cases — the same value/label split the formats
// vocabularies use. NOTE the confirmed GET sample returned businessIndustry
// "Retail" (title case), which would not match the id 'retail'; labelOf falls
// back to showing the raw stored value, so such a row stays readable rather
// than blank, but the two vocabularies need reconciling backend-side.

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import * as yup from 'yup';
import SettingsCard, { SettingsRow } from './SettingsCard';
import { labelOf, toSelectOptions, type SettingOption } from './companySettingsOptions';
import { Field, Input, SelectDropdown, Textarea } from '@/components/common';
import { countryOptions } from '@/mocks/countries';
import { employeeRanges, industries } from '@/mocks/onboarding';
import {
  useUpdateCompanyDetailsMutation,
  type CompanyDetails,
  type UpdateCompanyDetailsInput,
} from '@/api/endpoints/companySettingsApi';
import { CompanyEmployeeRange } from '@/api/endpoints/companyApi';
import { setAuth } from '@/redux/auth/authSlice';
import { selectCompanyDetail } from '@/redux/auth/selectors';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import type { ApiErrorShape } from '@/api/types';

interface CompanyDetailsCardProps {
  details: CompanyDetails;
}

type DetailsDraft = UpdateCompanyDetailsInput;

const seedOf = (d: CompanyDetails): DetailsDraft => ({
  name: d.name,
  email: d.email,
  website: d.website,
  industry: d.industry,
  address: d.address,
  country: d.country,
  employeeRange: d.employeeRange,
});

// Shaped as SettingOption so they share labelOf/toSelectOptions with the
// formats card — one label resolver, so read and edit modes cannot drift.
const INDUSTRIES: SettingOption[] = industries.map((i) => ({
  value: i.id,
  labelKey: i.labelKey,
}));

const EMPLOYEE_RANGES: SettingOption<CompanyEmployeeRange>[] = employeeRanges.map((r) => ({
  value: r.id,
  labelKey: r.labelKey,
}));

const emailSchema = yup.string().email();
const websiteSchema = yup.string().url();

const CompanyDetailsCard = ({ details }: CompanyDetailsCardProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const companyDetail = useAppSelector(selectCompanyDetail);
  const [updateDetails, { isLoading: saving }] = useUpdateCompanyDetailsMutation();

  const seed = useMemo(() => seedOf(details), [details]);
  const [draft, setDraft] = useState<DetailsDraft>(seed);
  const [editing, setEditing] = useState(false);
  useEffect(() => setDraft(seed), [seed]);

  const patch = (changes: Partial<DetailsDraft>) => setDraft((d) => ({ ...d, ...changes }));

  const nameMissing = !draft.name.trim();
  const emailInvalid = !!draft.email.trim() && !emailSchema.isValidSync(draft.email.trim());
  const websiteInvalid = !!draft.website.trim() && !websiteSchema.isValidSync(draft.website.trim());

  const dirty = (Object.keys(seed) as (keyof DetailsDraft)[]).some((k) => draft[k] !== seed[k]);
  const canSave = dirty && !nameMissing && !emailInvalid && !websiteInvalid;

  const cancel = () => {
    setDraft(seed);
    setEditing(false);
  };

  const save = async () => {
    const body: DetailsDraft = {
      ...draft,
      name: draft.name.trim(),
      email: draft.email.trim(),
      website: draft.website.trim(),
      industry: draft.industry.trim(),
      address: draft.address.trim(),
      country: draft.country.trim(),
    };
    try {
      await updateDetails(body).unwrap();
      setEditing(false);
      // The header and the switch-company row read the name from the auth
      // slice, which only select-company fills — patch it so a rename shows
      // immediately. Spread the existing object: setAuth is a top-level
      // Object.assign, so a partial companyDetail would drop _id/companyCode.
      if (companyDetail && body.name !== seed.name) {
        dispatch(setAuth({ companyDetail: { ...companyDetail, companyName: body.name } }));
      }
      toast.success(t('company_settings_details_saved'));
    } catch (err) {
      toast.error((err as ApiErrorShape)?.msg ?? t('error_generic'));
    }
  };

  return (
    <SettingsCard
      title={t('company_settings_details_title')}
      subtitle={t('company_settings_details_subtitle')}
      editing={editing}
      dirty={dirty}
      saving={saving}
      canSave={canSave}
      onEdit={() => setEditing(true)}
      onCancel={cancel}
      onSave={() => void save()}
    >
      {editing ? (
        <div className='company-settings__form'>
          <Field
            label={t('field_name')}
            required
            error={nameMissing ? t('company_settings_name_required') : undefined}
          >
            <Input
              value={draft.name}
              error={nameMissing}
              placeholder={t('company_settings_name_ph')}
              onChange={(e) => patch({ name: e.target.value })}
            />
          </Field>
          <Field
            label={t('company_settings_owner_email')}
            error={emailInvalid ? t('error_invalid_email') : undefined}
          >
            <Input
              type='email'
              dir='ltr'
              value={draft.email}
              error={emailInvalid}
              placeholder={t('company_settings_email_ph')}
              onChange={(e) => patch({ email: e.target.value })}
            />
          </Field>
          {/* Server-issued and absent from the payload, so it stays read-only
              here rather than disappearing when the card flips to edit. */}
          <SettingsRow label={t('company_settings_code')} value={details.code} locked ltr />
          <Field
            label={t('field_website')}
            optional
            error={websiteInvalid ? t('company_settings_website_invalid') : undefined}
          >
            <Input
              dir='ltr'
              iconStart='link'
              value={draft.website}
              error={websiteInvalid}
              placeholder={t('company_settings_website_ph')}
              onChange={(e) => patch({ website: e.target.value })}
            />
          </Field>
          <Field label={t('company_settings_industry')}>
            <SelectDropdown
              size='lg'
              options={toSelectOptions(INDUSTRIES, t)}
              value={draft.industry || null}
              onChange={(industry) => patch({ industry })}
            />
          </Field>
          <Field label={t('company_settings_address')} optional>
            <Textarea
              rows={2}
              value={draft.address}
              placeholder={t('company_settings_address_ph')}
              onChange={(e) => patch({ address: e.target.value })}
            />
          </Field>
          <Field label={t('field_country')} optional>
            <SelectDropdown
              size='lg'
              options={toSelectOptions(countryOptions, t)}
              value={draft.country || null}
              onChange={(country) => patch({ country })}
            />
          </Field>
          <Field label={t('company_settings_employee_range')}>
            <SelectDropdown<CompanyEmployeeRange>
              size='lg'
              options={toSelectOptions(EMPLOYEE_RANGES, t)}
              value={draft.employeeRange}
              onChange={(employeeRange) => patch({ employeeRange })}
            />
          </Field>
        </div>
      ) : (
        <div className='company-settings__rows'>
          <SettingsRow label={t('field_name')} value={details.name} />
          <SettingsRow label={t('company_settings_owner_email')} value={details.email} ltr />
          <SettingsRow label={t('company_settings_code')} value={details.code} locked ltr />
          <SettingsRow label={t('field_website')} value={details.website} ltr />
          <SettingsRow
            label={t('company_settings_industry')}
            value={labelOf(INDUSTRIES, details.industry, t)}
          />
          <SettingsRow label={t('company_settings_address')} value={details.address} />
          <SettingsRow
            label={t('field_country')}
            value={labelOf(countryOptions, details.country, t)}
          />
          <SettingsRow
            label={t('company_settings_employee_range')}
            value={labelOf(EMPLOYEE_RANGES, details.employeeRange, t)}
          />
        </div>
      )}
    </SettingsCard>
  );
};

export default CompanyDetailsCard;
